/**
 * Minimal custom element for the Recharge "Add to Cart" button.
 * Wires a click listener and exposes references to related controls:
 * - <payment-options-selector>
 * - <absorbency-selector>
 * - <delivery-frequency-dropdown>
 */
class RechargeAddToCartButton extends HTMLElement {
  constructor() {
    super();
    this._elements = {};
    this.storeIdentifier = Shopify.shop;
    this.productId = this.getAttribute('product-id') || null;
    this.handleClick = this.handleClick.bind(this);
    this.isAbsorbencyProduct = this.getAttribute('is-absorbency-product') === 'true';
    this.variantId = this.getAttribute('variant-id') || null;
    this.productFormId = this.getAttribute('product-form-id') || null;
    this.productType = this.getAttribute('product-type') || null;
    this.rechargeBundleCollectionId = this.getAttribute('recharge-bundle-collection-id') || null;
    this.rechargeBundleSubscriptionProductId = this.getAttribute('recharge-bundle-subscription-product-id') || null;
    this.oneTimePurchaseProductId = this.getAttribute('one-time-purchase-product-id') || null;
    this.oneTimePurchaseVariantId = this.getAttribute('one-time-purchase-variant-id') || null;
    this.hasDeliveryFrequencyProducts = this.getAttribute('has-delivery-frequency-products') === 'true';
    this.hasRegularRechargeSellingPlan = this.getAttribute('has-regular-recharge-selling-plan') === 'true';
    this.isStandardRechargeProduct = !this.hasDeliveryFrequencyProducts && this.hasRegularRechargeSellingPlan;
    this.button = this.querySelector('button');
    this.miniCart = document.querySelector('mini-cart');
  }

  connectedCallback() {
    this._elements.button = this.querySelector('button');
    if (this._elements.button) {
      this._elements.button.addEventListener('click', this.handleClick);
    }

    this.paymentOptionsSelector = this.productId ? document.getElementById(`payment-options-selector-${this.productId}`) : null;
    this.absorbencySelector = this.productId ? document.getElementById(`absorbency-selector-${this.productId}`) : null;
    this.deliveryFrequencyDropdown = this.productId ? document.getElementById(`delivery-frequency-dropdown-${this.productId}`) : null;
    this.bundleVariantSelector = this.productId ? document.getElementById(`bundle-variant-selector-${this.productId}`) : null;
    this.quantitySelector = document.getElementById(`Quantity-${this.productId}`);
    this.productForm = this.productFormId ? document.getElementById(this.productFormId) : null;
    recharge.init({ storeIdentifier: this.storeIdentifier });
  }

  disconnectedCallback() {
    if (this._elements.button) {
      this._elements.button.removeEventListener('click', this.handleClick);
    }
  }

  getCurrentVariantId = () => {
    return this.productForm?.querySelector('[name="id"]')?.value || this.variantId;
  }

  getTamponAbsorbencyData = (absorbency) => {
    // Used to show the quantity of each absorbency option in the cart.
    const cartProperties = {};

    // Used to pass the absorbency options to the Recharge API to get the bundle id.
    const absorbencySelections = absorbency.selections.filter(option => option.quantity > 0).map(option => {
      cartProperties[option.label] = parseInt(option.quantity) * parseInt(option.increment);
      return {
        quantity: option.quantity,
        externalProductId: this.rechargeBundleSubscriptionProductId,
        externalVariantId: option.bundleVariantId,
        collectionId: this.rechargeBundleCollectionId
      }
    });

    return {
      cartProperties,
      absorbencySelections
    }
  }

  getPadAbsorbencyData = (absorbency) => {
    // Used to show the quantity of each absorbency option in the cart.
    const cartProperties = {};
    const selectedOptions = absorbency.selections.filter(option => option.quantity > 0);

    selectedOptions.forEach(option => {
      cartProperties[option.label] = parseInt(option.quantity, 10) * parseInt(option.increment, 10);
    });

    // Used to pass the absorbency options to the Recharge API to get the bundle id.
    const padAbsorbencySelections = [];
    const [firstOption, secondOption] = selectedOptions;

    // Customers select pads in 6-count increments, but Recharge expects 12-count bundle quantities.
    // Convert the overlap between the two absorbency types into the mixed bundle first.
    const firstQuantity = parseInt(firstOption?.quantity || 0, 10);
    const secondQuantity = parseInt(secondOption?.quantity || 0, 10);
    const mixedQuantity = Math.min(firstQuantity, secondQuantity);
    const remainingFirstQuantity = firstQuantity - mixedQuantity;
    const remainingSecondQuantity = secondQuantity - mixedQuantity;
    const firstFullBundleQuantity = remainingFirstQuantity / 2;
    const secondFullBundleQuantity = remainingSecondQuantity / 2;

    // After removing the mixed bundles, the remaining single-absorbency quantities should be
    // even so they can be represented as full 12-count bundles in Recharge.
    if (!Number.isInteger(firstFullBundleQuantity) || !Number.isInteger(secondFullBundleQuantity)) {
      console.warn('[recharge-add-to-cart] Pad absorbency selections could not be converted into 12-count Recharge bundles', {
        selectedOptions
      });
    }

    if (mixedQuantity > 0 && firstOption?.padsMixedVariantId) {
      padAbsorbencySelections.push({
        quantity: mixedQuantity,
        label: 'Mixed',
        externalProductId: this.rechargeBundleSubscriptionProductId,
        externalVariantId: firstOption.padsMixedVariantId,
        collectionId: this.rechargeBundleCollectionId
      });
    }

    if (Number.isInteger(firstFullBundleQuantity) && firstFullBundleQuantity > 0 && firstOption) {
      padAbsorbencySelections.push({
        quantity: firstFullBundleQuantity,
        externalProductId: this.rechargeBundleSubscriptionProductId,
        externalVariantId: firstOption.bundleVariantId,
        collectionId: this.rechargeBundleCollectionId
      });
    }

    if (Number.isInteger(secondFullBundleQuantity) && secondFullBundleQuantity > 0 && secondOption) {
      padAbsorbencySelections.push({
        quantity: secondFullBundleQuantity,
        externalProductId: this.rechargeBundleSubscriptionProductId,
        externalVariantId: secondOption.bundleVariantId,
        collectionId: this.rechargeBundleCollectionId
      });
    }

    return {
      cartProperties,
      // Remove any zero-quantity selections before sending the bundle request to Recharge.
      absorbencySelections: padAbsorbencySelections.filter(selection => selection.quantity > 0)
    }
  }

  // Taken from ProductForm in global.js 
  handleErrorMessage(errorMessage = false) {
    this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
    
    if (this.errorMessageWrapper) {
      if (this.hideErrors) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
    else {
      if (errorMessage) {
        alert(errorMessage);
      }
    }
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.button.setAttribute('aria-disabled', true);
      this.button.classList.add('loading');
      this.button.disabled = true;
    } else {
      this.button.setAttribute('aria-disabled', false);
      this.button.classList.remove('loading');
      this.button.disabled = false;
    }
  }

  handleClick = async (event) => {
    event.preventDefault();
    this.setLoadingState(true);
    this.handleErrorMessage(); // Clear any previous error messages.

    const hasAbsorbencyError = this.absorbencySelector?.getCurrentSelection()?.selectionValid === false;
    if (hasAbsorbencyError) {
      const maxCount = this.absorbencySelector?.getCurrentSelection()?.maxCount;
      this.handleErrorMessage(`Please select a total of ${maxCount} ${this.productType === 'tampon' ? 'tampons' : 'pads'}`);
      this.setLoadingState(false);
      return;
    }

    try {
      // Used to get the Recharge bundle ID for a specific bundle configuration.
      const RECHARGE_BUNDLE_KEY = '_rb_id';

      const quantity = this.quantitySelector?.value || 1; // This is not the quantity of the bundle selections, this is the overall quantity the customer selected on the PDP
      const isSubscription = this.paymentOptionsSelector?.value === 'subscription';
      const hasAbsorbency = this.isAbsorbencyProduct;
      const deliveryFrequency = this.deliveryFrequencyDropdown?.getCurrentSelection();
      const absorbency = this.absorbencySelector?.getCurrentSelection();
      const productType = this.productType;

      // The selection quantity comes from the absorbency_quantity metafield on the frequency product.
      // Used for bundle selection quantity.
      const subscriptionSelectionQuantity = parseInt(deliveryFrequency?.maxQuantityMultiplier || 0, 10);
      const isBundleWithoutSubscription = subscriptionSelectionQuantity == 0;

      // Begin standard Recharge product flow.
      if (this.isStandardRechargeProduct) {
        const currentVariantId = this.getCurrentVariantId();
        const sellingPlanId = isSubscription ? (deliveryFrequency?.sellingPlanId || null) : null;

        if (isSubscription && !sellingPlanId) {
          this.handleErrorMessage('Please select a subscription option before adding to cart.');
          this.setLoadingState(false);
          return;
        }

        // If on cart page or cart drawer disabled, let it behave like BeYours (no drawer interception)
        if (document.body.classList.contains('template-cart') || !theme?.shopSettings?.cartDrawer) {
          // Optional: redirect to cart after add, or just fall back to normal add
          // window.location.href = theme.routes.cart_url || '/cart';
          // For now, we’ll still do the ajax add but not attempt drawer rendering.
        }

        // Below we will build the form data like the theme ProductForm class does so that we can interact with the cart drawer using the same code as the theme.
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        // Build FormData like theme ProductForm does
        const formData = new FormData();
        formData.append('id', currentVariantId);
        formData.append('quantity', String(quantity));

        // Selling plan (only when subscription)
        if (sellingPlanId) {
          formData.append('selling_plan', String(sellingPlanId));
        }

        // Sections payload so mini-cart can re-render immediately
        if (this.miniCart) {
          formData.append(
            'sections',
            this.miniCart.getSectionsToRender().map((section) => section.id)
          );
        }
        formData.append('sections_url', window.location.pathname);

        config.body = formData;

        const response = await fetch(`${theme.routes.cart_add_url}`, config);
        const json = await response.json();

        // BeYours uses `response.status` to indicate an error
        if (json.status) {
          try {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'recharge-add-to-cart',
              productVariantId: currentVariantId,
              errors: json.errors || json.description,
              message: json.message,
            });
          } catch (_) {} // Ignore error and handle later

          this.handleErrorMessage(json.description || json.message || 'Unable to add to cart.');
          return;
        }

        // Publish cart update like theme
        try {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'recharge-add-to-cart',
            productVariantId: currentVariantId,
          });
        } catch (_) {} // Ignore error and handle later

        // Render mini-cart contents like theme
        if (this.miniCart) {
          this.miniCart.renderContents(json);
        }

        return;
      }
      // End standard Recharge product flow.

      // Begin bundle flow.
      const oneTimeProductId = this.oneTimePurchaseProductId;
      const oneTimeVariantId = this.oneTimePurchaseVariantId;
      const subscriptionProductId = deliveryFrequency?.frequencyProductId;
      const subscriptionVariantId = deliveryFrequency?.frequencyProductVariantId;
      const subscriptionSellingPlanId = deliveryFrequency?.sellingPlanId;

      // Get the data for the bundle payload based on the subscription or one-time purchase.
      const productBundleData = {
        quantity,
        productId: isSubscription ? subscriptionProductId : oneTimeProductId,
        variantId: isSubscription ? subscriptionVariantId : oneTimeVariantId,
        sellingPlanId: isSubscription ? subscriptionSellingPlanId : null,
      }

      // Used to show the quantity of each absorbency option in the cart.
      let cartProperties = {};
      let rechargeBundleData = {};

      if (hasAbsorbency) {
        let absorbencySelections = [];

        if (productType === 'tampon') {
          const tamponAbsorbencyData = this.getTamponAbsorbencyData(absorbency);
          cartProperties = tamponAbsorbencyData.cartProperties;
          absorbencySelections = tamponAbsorbencyData.absorbencySelections;
        } else if (productType === 'pad') {
          const padAbsorbencyData = this.getPadAbsorbencyData(absorbency);
          cartProperties = padAbsorbencyData.cartProperties;
          absorbencySelections = padAbsorbencyData.absorbencySelections;
        }

        rechargeBundleData = {
          externalProductId: productBundleData.productId,
          externalVariantId: productBundleData.variantId,
          selections: absorbencySelections,
        }
      } else if (this.bundleVariantSelector && !isBundleWithoutSubscription) {

        // Handle non-absorbency products.
        const selectedVariantData = this.bundleVariantSelector.getCurrentSelection();

        const selection = {
          collectionId: this.rechargeBundleCollectionId,
          quantity: parseInt(subscriptionSelectionQuantity, 10),
          externalProductId: selectedVariantData.subscriptionProductId,
          externalVariantId: selectedVariantData.variantId,
        }

        rechargeBundleData = {
          externalProductId: productBundleData.productId,
          externalVariantId: productBundleData.variantId,
          selections: [selection],
        }

        // Only add the variant title to the cart properties if it's not the onlydefault variant.
        if (!selectedVariantData.isDefault) {
        cartProperties[selectedVariantData.rechargeBundleVariantOptionName] = selectedVariantData.variantTitle;
        }
      } else {
        rechargeBundleData = {
          externalProductId: productBundleData.productId,
          externalVariantId: productBundleData.variantId,
          selections: []
        }
      }

      const rechargeBundleId = await recharge.bundle.getBundleId(rechargeBundleData);
      cartProperties[RECHARGE_BUNDLE_KEY] = rechargeBundleId;

      // If on cart page or cart drawer disabled, let it behave like BeYours (no drawer interception)
      if (document.body.classList.contains('template-cart') || !theme?.shopSettings?.cartDrawer) {
        // Optional: redirect to cart after add, or just fall back to normal add
        // window.location.href = theme.routes.cart_url || '/cart';
        // For now, we’ll still do the ajax add but not attempt drawer rendering.
      }

      // Below we will build the form data like the theme ProductForm class does so that we can interact with the cart drawer using the same code as the theme.
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      // Build FormData like theme ProductForm does
      const formData = new FormData();
      formData.append('id', productBundleData.variantId);
      formData.append('quantity', String(productBundleData.quantity));

      // Selling plan (only when subscription)
      if (productBundleData.sellingPlanId) {
        formData.append('selling_plan', String(productBundleData.sellingPlanId));
      }

      // Line item properties (Recharge bundle id + your labels)
      Object.entries(cartProperties || {}).forEach(([key, value]) => {
        formData.append(`properties[${key}]`, String(value));
      });

      // Sections payload so mini-cart can re-render immediately
      if (this.miniCart) {
        formData.append(
          'sections',
          this.miniCart.getSectionsToRender().map((section) => section.id)
        );
      }
      formData.append('sections_url', window.location.pathname);

      config.body = formData;

      const response = await fetch(`${theme.routes.cart_add_url}`, config);
      const json = await response.json();

      // BeYours uses `response.status` to indicate an error
      if (json.status) {
        // Match theme’s error publishing behavior (if pubsub is present)
        try {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'recharge-add-to-cart',
            productVariantId: productBundleData.variantId,
            errors: json.errors || json.description,
            message: json.message,
          });
        } catch (_) {} // Ignore error and handle later

        this.handleErrorMessage(json.description || json.message || 'Unable to add to cart.');
        return;
      }

      // Publish cart update like theme
      try {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'recharge-add-to-cart',
          productVariantId: productBundleData.variantId,
        });
      } catch (_) {} // Ignore error and handle later

      // Render mini-cart contents like theme
      if (this.miniCart) {
        this.miniCart.renderContents(json);
      }

    } catch (error) {
      this.setLoadingState(false);
      console.log('*** Add to cart error: ', error);
      this.handleErrorMessage('An error occurred while adding the product to the cart. Please try again or contact support.');
    } finally {
      this.setLoadingState(false);
    }
  }
}

if (!customElements.get('recharge-add-to-cart-button')) {
  customElements.define('recharge-add-to-cart-button', RechargeAddToCartButton);
}

// // Optional sample listener (safe no-op if element not present)
// document.addEventListener('DOMContentLoaded', () => {
//   const el = document.querySelector('recharge-add-to-cart-button');
//   if (!el) return;
//   el.addEventListener('click', () => {
//     console.log('Recharge add-to-cart clicked');
//   });
// });
